/**
 * PostHog (client) — browser SDK wrapper.
 *
 * Mirror of `posthog.server.ts` for client-side captures. Same design rules:
 *   - No-op when `NEXT_PUBLIC_POSTHOG_KEY` is empty.
 *   - Initialized exactly once via `initPostHogClient()` from the React provider.
 *   - Every payload runs through `sanitizeEventProps` before capture.
 *   - Distinct-id is the SHA-256-hashed user id (see `hashUserId`); raw oid /
 *     email never reach PostHog.
 *
 * Privacy hardening:
 *   - `person_profiles: 'identified_only'` — anonymous visitors don't create
 *     profile rows we'd later need to delete on a GDPR request.
 *   - `mask_all_text: true` for session replay (replay is OFF by default; this
 *     is belt-and-suspenders if it's ever flipped on).
 *   - `capture_pageview: false` + `capture_pageleave: false` — we capture
 *     pageviews manually after stripping query strings, so we don't leak emails
 *     embedded in URL params (e.g. password reset links).
 */

"use client";

import posthogJs, { type PostHog } from "posthog-js";
import { sanitizeEventProps, scrubString } from "@/lib/log/sanitize";
import {
    EVENT_KEEP_KEYS,
    type EventName,
    type EventPayloadMap,
} from "@/lib/analytics/events";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY ?? "";
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.i.posthog.com";

let initialized = false;
let disabled = false;

export function isPostHogEnabled(): boolean {
    return !!POSTHOG_KEY && !disabled;
}

export function initPostHogClient(): PostHog | null {
    if (typeof window === "undefined") return null;
    if (initialized) return posthogJs;
    if (!POSTHOG_KEY) {
        disabled = true;
        return null;
    }

    posthogJs.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        person_profiles: "identified_only",
        capture_pageview: false,
        capture_pageleave: false,
        // Disable the DOM-event recording surface — explicit, typed events only.
        autocapture: false,
        session_recording: {
            maskAllInputs: true,
            maskTextSelector: "*",
        },
        request_batching: true,
    });

    initialized = true;
    return posthogJs;
}

export function identifyUser(hashedUserId: string, traits?: Record<string, unknown>): void {
    if (!isPostHogEnabled() || !initialized) return;
    try {
        posthogJs.identify(hashedUserId, traits ? sanitizeEventProps(traits) : undefined);
    } catch (err) {
        console.error("[posthog.client] identify failed:", err);
    }
}

export function resetUser(): void {
    if (!isPostHogEnabled() || !initialized) return;
    try {
        posthogJs.reset();
    } catch (err) {
        console.error("[posthog.client] reset failed:", err);
    }
}

export function trackClient<K extends EventName>(
    name: K,
    props: EventPayloadMap[K],
): void {
    if (!isPostHogEnabled() || !initialized) return;
    try {
        const sanitized = sanitizeEventProps(props as unknown as Record<string, unknown>, {
            keepKeys: [...EVENT_KEEP_KEYS],
        });
        posthogJs.capture(name, {
            ...sanitized,
            $lib: "savira-client",
            source: "client",
        });
    } catch (err) {
        console.error("[posthog.client] capture failed:", err);
    }
}

export function capturePageview(pathname: string): void {
    if (!isPostHogEnabled() || !initialized) return;
    try {
        const safePath = scrubString(pathname);
        posthogJs.capture("$pageview", {
            $current_url: safePath,
            $pathname: safePath,
        });
    } catch (err) {
        console.error("[posthog.client] pageview failed:", err);
    }
}
